import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  DocumentVariable, 
  VariableValue, 
  DEFAULT_DOCUMENT_VARIABLES,
  formatVariableValue,
  validateVariableValue
} from '../types/documentVariables';

const VARIABLES_COLLECTION = 'documentVariables';
const VALUES_COLLECTION = 'documentVariableValues';

export class DocumentVariablesService {
  
  // ========== VÁLTOZÓK KEZELÉSE ==========
  
  /**
   * Szervezet összes változójának lekérése
   */
  static async getOrganizationVariables(organizationId: string): Promise<DocumentVariable[]> {
    const q = query(
      collection(db, VARIABLES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      orderBy('category'),
      orderBy('order')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as DocumentVariable));
  }
  
  /**
   * Sablon specifikus változók lekérése
   */
  static async getTemplateVariables(organizationId: string, templateId: string): Promise<DocumentVariable[]> {
    // Két külön query: template-specifikus és globális változók
    const templateQuery = query(
      collection(db, VARIABLES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      where('templateId', '==', templateId)
    );
    
    // Globális változók: azok, amelyeknek nincs templateId mezője
    // Firebase-ben az undefined mezők nem léteznek, ezért külön query kell
    const allVariablesQuery = query(
      collection(db, VARIABLES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true)
    );
    
    // Mindkét query végrehajtása
    const [templateSnapshot, allSnapshot] = await Promise.all([
      getDocs(templateQuery),
      getDocs(allVariablesQuery)
    ]);
    
    // Template-specifikus változók
    const templateVars = templateSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as DocumentVariable));
    
    // Globális változók: azok, amelyeknek nincs templateId mezője vagy null
    const globalVars = allSnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as DocumentVariable))
      .filter(variable => !variable.templateId || variable.templateId === null);
    
    // Duplikátumok eltávolítása (ha egy változó mind template-specifikus, mind globális)
    const templateVarIds = new Set(templateVars.map(v => v.id));
    const uniqueGlobalVars = globalVars.filter(v => !templateVarIds.has(v.id));
    
    // Összes változó visszaadása, rendezve
    const allVars = [...templateVars, ...uniqueGlobalVars];
    return allVars.sort((a, b) => {
      const categoryA = a.category || '';
      const categoryB = b.category || '';
      if (categoryA !== categoryB) {
        return categoryA.localeCompare(categoryB);
      }
      return (a.order || 0) - (b.order || 0);
    });
  }
  
  /**
   * Új változó létrehozása
   */
  static async createVariable(
    variable: Omit<DocumentVariable, 'id' | 'createdAt' | 'updatedAt'>,
    userId: string
  ): Promise<string> {
    const docRef = await addDoc(collection(db, VARIABLES_COLLECTION), {
      ...variable,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId
    });
    
    return docRef.id;
  }
  
  /**
   * Változó frissítése
   */
  static async updateVariable(
    variableId: string, 
    updates: Partial<DocumentVariable>
  ): Promise<void> {
    const docRef = doc(db, VARIABLES_COLLECTION, variableId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }
  
  /**
   * Változó törlése (soft delete)
   */
  static async deleteVariable(variableId: string): Promise<void> {
    const docRef = doc(db, VARIABLES_COLLECTION, variableId);
    await updateDoc(docRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });
  }
  
  /**
   * Alapértelmezett változók inicializálása szervezet számára
   */
  static async initializeDefaultVariables(organizationId: string, userId: string): Promise<void> {
    const batch = writeBatch(db);
    
    DEFAULT_DOCUMENT_VARIABLES.forEach(variable => {
      const docRef = doc(collection(db, VARIABLES_COLLECTION));
      batch.set(docRef, {
        ...variable,
        id: docRef.id,
        organizationId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      });
    });
    
    await batch.commit();
  }
  
  // ========== VÁLTOZÓ ÉRTÉKEK KEZELÉSE ==========
  
  /**
   * Változó értékek lekérése kontextus alapján
   */
  static async getVariableValues(
    organizationId: string,
    context: {
      templateId?: string;
      customerId?: string;
      documentId?: string;
    }
  ): Promise<Record<string, string>> {
    const conditions = [
      where('organizationId', '==', organizationId)
    ];
    
    if (context.templateId) {
      conditions.push(where('templateId', '==', context.templateId));
    }
    if (context.customerId) {
      conditions.push(where('customerId', '==', context.customerId));
    }
    if (context.documentId) {
      conditions.push(where('documentId', '==', context.documentId));
    }
    
    const q = query(collection(db, VALUES_COLLECTION), ...conditions);
    const snapshot = await getDocs(q);
    
    const values: Record<string, string> = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data() as VariableValue;
      values[data.variableId] = data.value;
    });
    
    return values;
  }
  
  /**
   * Változó értékek mentése
   */
  static async saveVariableValues(
    organizationId: string,
    values: Record<string, string>,
    context: {
      templateId?: string;
      customerId?: string;
      documentId?: string;
    }
  ): Promise<void> {
    const batch = writeBatch(db);
    
    for (const [variableId, value] of Object.entries(values)) {
      if (!value) continue; // Üres értékeket nem mentjük
      
      // Keresés meglévő értékre
      const q = query(
        collection(db, VALUES_COLLECTION),
        where('organizationId', '==', organizationId),
        where('variableId', '==', variableId),
        ...(context.templateId ? [where('templateId', '==', context.templateId)] : []),
        ...(context.customerId ? [where('customerId', '==', context.customerId)] : []),
        ...(context.documentId ? [where('documentId', '==', context.documentId)] : [])
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Új érték létrehozása
        const docRef = doc(collection(db, VALUES_COLLECTION));
        batch.set(docRef, {
          id: docRef.id,
          variableId,
          value,
          organizationId,
          ...context,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Meglévő érték frissítése
        const existingDoc = snapshot.docs[0];
        batch.update(existingDoc.ref, {
          value,
          updatedAt: serverTimestamp()
        });
      }
    }
    
    await batch.commit();
  }
  
  // ========== DOKUMENTUM GENERÁLÁS TÁMOGATÁS ==========
  
  /**
   * Változók és értékek összeállítása dokumentum generáláshoz
   */
  static async prepareDocumentData(
    organizationId: string,
    templateId: string,
    customerId?: string,
    additionalValues: Record<string, string> = {}
  ): Promise<Record<string, string>> {
    // Változók lekérése
    const variables = await this.getTemplateVariables(organizationId, templateId);
    
    // Mentett értékek lekérése
    const savedValues = await this.getVariableValues(organizationId, {
      templateId,
      customerId
    });
    
    // Dokumentum adatok összeállítása
    const documentData: Record<string, string> = {};
    
    variables.forEach(variable => {
      let value = '';
      
      // Prioritási sorrend: additionalValues > savedValues > defaultValue
      if (additionalValues[variable.key]) {
        value = additionalValues[variable.key];
      } else if (savedValues[variable.id]) {
        value = savedValues[variable.id];
      } else if (variable.defaultValue) {
        value = variable.defaultValue;
      }
      
      // Speciális értékek automatikus kitöltése
      if (variable.key === 'CURRENT_DATE' && !value) {
        value = new Date().toISOString().split('T')[0];
      }
      
      // Érték formázása
      documentData[variable.key] = formatVariableValue(variable, value);
    });
    
    return documentData;
  }
  
  /**
   * Változók validálása dokumentum generálás előtt
   */
  static async validateDocumentData(
    organizationId: string,
    templateId: string,
    values: Record<string, string>
  ): Promise<{ isValid: boolean; errors: Record<string, string> }> {
    const variables = await this.getTemplateVariables(organizationId, templateId);
    const errors: Record<string, string> = {};
    
    // Változók kulcs alapú indexelése
    const variablesByKey = variables.reduce((acc, variable) => {
      acc[variable.key] = variable;
      return acc;
    }, {} as Record<string, DocumentVariable>);
    
    // Validáció
    for (const [key, value] of Object.entries(values)) {
      const variable = variablesByKey[key];
      if (!variable) continue;
      
      const error = validateVariableValue(variable, value);
      if (error) {
        errors[key] = error;
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
  
  /**
   * Sablon változók automatikus felismerése dokumentumból
   */
  static extractVariablesFromTemplate(templateContent: string): string[] {
    // Különböző placeholder formátumok felismerése
    const patterns = [
      /\{\{([^}]+)\}\}/g,           // {{VARIABLE}}
      /\$\{([^}]+)\}/g,            // ${VARIABLE}
      /\[([A-Z_][A-Z0-9_]*)\]/g,   // [VARIABLE]
      /%([A-Z_][A-Z0-9_]*)%/g      // %VARIABLE%
    ];
    
    const variables = new Set<string>();
    
    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(templateContent)) !== null) {
        variables.add(match[1].trim().toUpperCase());
      }
    });
    
    return Array.from(variables);
  }
  
  /**
   * Hiányzó változók automatikus létrehozása
   */
  static async createMissingVariables(
    organizationId: string,
    templateId: string,
    variableKeys: string[],
    userId: string
  ): Promise<void> {
    const existingVariables = await this.getTemplateVariables(organizationId, templateId);
    const existingKeys = new Set(existingVariables.map(v => v.key));
    
    const missingKeys = variableKeys.filter(key => !existingKeys.has(key));
    
    if (missingKeys.length === 0) return;
    
    const batch = writeBatch(db);
    
    missingKeys.forEach((key, index) => {
      const docRef = doc(collection(db, VARIABLES_COLLECTION));
      batch.set(docRef, {
        id: docRef.id,
        name: key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        key,
        type: 'text',
        scope: 'template',
        category: 'custom',
        templateId,
        organizationId,
        order: 1000 + index, // Végére rakjuk
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      });
    });
    
    await batch.commit();
  }
}

export default DocumentVariablesService;
